'use client';

import { Suspense } from 'react';

import { useRouter } from '../../utils/hooks/useRouter.ts';
import { Link, type LinkProps } from './link.tsx';

/**
 * Resolves the current pathname and sets aria-current on the link
 * when it matches. Rendered inside Suspense so the pathname lookup
 * doesn't block the static shell during prerender.
 */
const NavLinkWithPathname: React.FC<LinkProps> = (props) => {
  const { usePathname } = useRouter();
  // eslint-disable-next-line react-compiler/react-compiler -- usePathname is a framework-agnostic hook passed via RouterContext
  const pathname = usePathname();
  const isCurrent = pathname === props.href;

  return (
    <Link {...props} {...(isCurrent && { 'aria-current': 'page' as const })} />
  );
};

/**
 * A navigation-aware link that sets `aria-current="page"` when the link's
 * href matches the current pathname. Use this in navs (header, footer,
 * sidebar) where active state indication matters.
 *
 * For body content links, use `<Link>` directly — it's lighter and doesn't
 * subscribe to pathname changes.
 */
export const NavLink: React.FC<LinkProps> = (props) => (
  <Suspense fallback={<Link {...props} />}>
    <NavLinkWithPathname {...props} />
  </Suspense>
);
